import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Lock, Zap, Flame, ArrowRight, Check, Sparkles, Layers } from 'lucide-react';
import { KITS } from '../data/kits';
import { UPSELLS } from '../data/upsells';
import { PaymentMethods } from './PaymentMethods';
type SecondOptionType = 'kit' | 'complement';
interface SecondOption {
  id: string;
  name: string;
  price: number;
  type: SecondOptionType;
  colorClass?: string;
  colorHex?: string;
}
interface SecondKitUpsellPopupProps {
  open: boolean;
  selectedKitIds: string[];
  onAccept: (secondId: string, type: SecondOptionType) => void;
  onDecline: () => void;
}
// Fixed price for taking 2 products together.
const TWO_KITS_PRICE = 22990;
export function SecondKitUpsellPopup({
  open,
  selectedKitIds,
  onAccept,
  onDecline
}: SecondKitUpsellPopupProps) {
  const currentKit = KITS.find(k => k.id === selectedKitIds[0]) ?? KITS[0];
  // Second product can be any of the OTHER main kits, OR any complementary kit.
  const options: SecondOption[] = [...KITS.filter(k => !selectedKitIds.includes(k.id)).map(k => ({
    id: k.id,
    name: k.name,
    price: k.price,
    type: 'kit' as const,
    colorClass: k.color
  })), ...UPSELLS.map(u => ({
    id: u.id,
    name: u.name,
    price: u.price,
    type: 'complement' as const,
    colorHex: u.color
  }))];
  const [chosenId, setChosenId] = useState(options[0]?.id ?? KITS[0].id);
  useEffect(() => {
    if (open && options.length > 0 && !options.some(o => o.id === chosenId)) {
      setChosenId(options[0].id);
    }
  }, [open, options, chosenId]);
  const chosenOption = options.find(o => o.id === chosenId) ?? options[0];
  const normalTwo = currentKit.price + (chosenOption?.price ?? 0);
  const savings = normalTwo - TWO_KITS_PRICE;
  useEffect(() => {
    if (open) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = prev;
      };
    }
  }, [open]);
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onDecline();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, onDecline]);
  return <AnimatePresence data-id="element-869">
      {open && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.2
    }} className="fixed inset-0 z-[85] flex items-center justify-center p-3 sm:p-6 bg-black/70 backdrop-blur-sm overflow-y-auto" onClick={onDecline} role="dialog" aria-modal="true" aria-labelledby="second-kit-title" data-id="element-870">
          <motion.div initial={{
        opacity: 0,
        y: 30,
        scale: 0.96
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: 30,
        scale: 0.96
      }} transition={{
        duration: 0.3,
        ease: 'easeOut'
      }} onClick={e => e.stopPropagation()} className="relative bg-cream rounded-[1.75rem] shadow-2xl w-full max-w-lg overflow-hidden my-4 max-h-[95vh] flex flex-col" data-id="element-871">
            <button onClick={onDecline} aria-label="Cerrar" className="absolute top-3 right-3 z-10 w-9 h-9 rounded-full bg-white/90 hover:bg-white text-ink-light hover:text-ink flex items-center justify-center shadow-md transition-colors" data-id="element-872">
              <X className="w-4 h-4" data-id="element-873" />
            </button>
            {/* Header banner */}
            <div className="relative bg-gradient-to-br from-red-600 via-red-700 to-red-900 text-white px-4 sm:px-5 pt-3 sm:pt-5 pb-4 sm:pb-5 text-center overflow-hidden" data-id="element-874">
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_0%,rgba(252,211,77,0.25),transparent_55%)]" data-id="element-875" />
              <div className="absolute -top-10 -left-10 w-40 h-40 bg-white/5 rounded-full blur-2xl" data-id="element-876" />
              <div className="absolute -bottom-12 -right-10 w-44 h-44 bg-black/20 rounded-full blur-2xl" data-id="element-877" />
              <motion.div initial={{
            scale: 0,
            rotate: -10
          }} animate={{
            scale: 1,
            rotate: 0
          }} transition={{
            type: 'spring',
            stiffness: 260,
            damping: 18,
            delay: 0.05
          }} className="relative inline-flex mb-1.5" data-id="element-878">
                <motion.div animate={{
              scale: [1, 1.08, 1]
            }} transition={{
              duration: 1.6,
              repeat: Infinity
            }} className="absolute inset-0 bg-yellow-300/40 rounded-full blur-md" data-id="element-879" />
                <div className="relative w-8 h-8 sm:w-12 sm:h-12 rounded-full bg-yellow-400 text-red-800 flex items-center justify-center shadow-lg border-2 sm:border-[3px] border-white/20" data-id="element-880">
                  <Layers className="w-4 h-4 sm:w-6 sm:h-6" strokeWidth={2.5} data-id="element-881" />
                </div>
              </motion.div>
              <motion.div animate={{
            scale: [1, 1.03, 1]
          }} transition={{
            duration: 1.5,
            repeat: Infinity
          }} className="relative inline-flex items-center gap-1 sm:gap-1.5 bg-white/15 backdrop-blur-sm text-yellow-200 px-2 sm:px-2.5 py-0.5 rounded-full font-extrabold text-[8px] sm:text-[10px] uppercase tracking-[0.12em] sm:tracking-[0.16em] mb-1.5 sm:mb-2 border border-yellow-300/40" data-id="element-882">
                <Flame className="w-2 h-2 sm:w-2.5 sm:h-2.5" data-id="element-883" />
                Oferta exclusiva · Solo aparece una vez
                <Flame className="w-2 h-2 sm:w-2.5 sm:h-2.5" data-id="element-884" />
              </motion.div>
              <h2 id="second-kit-title" className="relative font-sans font-extrabold text-[15px] sm:text-2xl leading-tight tracking-tight mb-1 sm:mb-1.5" data-id="element-885">
                ¡Sumá un segundo producto!
              </h2>
              <p className="relative text-[11px] sm:text-sm text-white/95 max-w-sm mx-auto leading-snug" data-id="element-886">
                Llevate{' '}
                <span className="font-bold text-yellow-300 underline decoration-yellow-300/60 underline-offset-2" data-id="element-887">
                  2 productos por solo ${TWO_KITS_PRICE.toLocaleString('es-AR')}
                </span>
                .
              </p>
            </div>
            {/* Body */}
            <div className="px-4 sm:px-6 pt-3 sm:pt-4 pb-3 sm:pb-4 flex-1 overflow-y-auto" data-id="element-888">
              <div className="flex justify-center mb-2.5" data-id="element-889">
                <motion.div animate={{
              opacity: [1, 0.55, 1],
              scale: [1, 1.04, 1]
            }} transition={{
              duration: 1,
              repeat: Infinity
            }} className="bg-gradient-to-r from-combo-green to-combo-green/80 text-white px-3 py-1 rounded-full font-extrabold text-[10px] sm:text-[11px] shadow-glow border-2 border-white tracking-wide" data-id="element-890">
                  🎁 2 PRODUCTOS · 1 PRECIO ESPECIAL
                </motion.div>
              </div>
              <p className="text-[12px] sm:text-sm text-ink leading-snug mb-3 text-center" data-id="element-891">
                Ya elegiste <strong data-id="element-892">{currentKit.name}</strong>. Sumá un segundo
                producto a elección y llevate{' '}
                <strong className="text-combo-green" data-id="element-893">
                  ambos por ${TWO_KITS_PRICE.toLocaleString('es-AR')}
                </strong>{' '}
                en vez de ${normalTwo.toLocaleString('es-AR')}.
              </p>
              {/* Second product chooser */}
              <div className="bg-white rounded-xl p-3 shadow-sm border border-ink/5 mb-2.5" data-id="element-894">
                <div className="font-bold text-[10px] text-ink uppercase tracking-wider mb-2 flex items-center gap-1.5" data-id="element-895">
                  <Sparkles className="w-3 h-3" style={{
                color: '#C9A84C'
              }} data-id="element-896" />
                  Elegí tu segundo producto:
                </div>
                <div className="grid grid-cols-1 gap-1.5" data-id="element-897">
                  {options.map(o => {
                const active = o.id === chosenId;
                return <button key={o.id} type="button" onClick={() => setChosenId(o.id)} className={`flex items-center gap-2 text-left px-2.5 py-2 rounded-lg border transition-all ${active ? 'border-combo-green bg-combo-green/5 ring-1 ring-combo-green' : 'border-ink/10 hover:border-ink/30'}`} data-id="element-898">
                        <span className={`w-3 h-3 rounded-full flex-shrink-0 ${o.colorClass ?? ''}`} style={o.colorHex ? {
                    backgroundColor: o.colorHex
                  } : undefined} data-id="element-899" />
                        <span className="text-[12px] font-medium text-ink leading-tight flex-1 min-w-0" data-id="element-900">
                          {o.name}
                        </span>
                        <span className="text-[9px] font-bold uppercase tracking-wide text-ink-light flex-shrink-0" data-id="element-901">
                          {o.type === 'kit' ? 'Principal' : 'Complemento'}
                        </span>
                        {active && <Check className="w-3.5 h-3.5 text-combo-green flex-shrink-0" strokeWidth={3} data-id="element-902" />}
                      </button>;
              })}
                </div>
              </div>
              {/* What's included */}
              <div className="bg-white rounded-xl p-3 shadow-sm border border-ink/5 mb-2.5" data-id="element-903">
                <div className="font-bold text-[10px] text-ink uppercase tracking-wider mb-1.5" data-id="element-904">
                  ✅ Tus 2 productos:
                </div>
                <ul className="space-y-1" data-id="element-905">
                  <li className="flex items-start gap-1.5 text-[11px] sm:text-[12px] text-ink leading-snug" data-id="element-906">
                    <Check className="w-3 h-3 text-combo-green flex-shrink-0 mt-0.5" strokeWidth={3} data-id="element-907" />
                    <span data-id="element-908">
                      <strong data-id="element-909">{currentKit.name}</strong>{' '}
                      <span className="text-ink-light text-[10px]" data-id="element-910">
                        (ya elegido)
                      </span>
                    </span>
                  </li>
                  <li className="flex items-start gap-1.5 text-[11px] sm:text-[12px] text-ink leading-snug" data-id="element-911">
                    <Check className="w-3 h-3 text-combo-green flex-shrink-0 mt-0.5" strokeWidth={3} data-id="element-912" />
                    <span data-id="element-913">
                      <strong data-id="element-914">{chosenOption?.name}</strong>{' '}
                      <span className="text-ink-light text-[10px]" data-id="element-915">
                        (
                        {chosenOption?.type === 'kit' ? 'kit principal' : 'complemento'}
                        )
                      </span>
                    </span>
                  </li>
                  <li className="flex items-start gap-1.5 text-[11px] sm:text-[12px] text-ink leading-snug" data-id="element-916">
                    <Check className="w-3 h-3 text-combo-green flex-shrink-0 mt-0.5" strokeWidth={3} data-id="element-917" />
                    <span data-id="element-918">
                      <strong data-id="element-919">Acceso de por vida</strong> + actualizaciones
                    </span>
                  </li>
                </ul>
              </div>
              {/* Pricing */}
              <div className="bg-gradient-to-br from-cream-dark to-cream-dark/80 rounded-xl p-3 border border-gold/30" data-id="element-920">
                <div className="flex items-center justify-between gap-3" data-id="element-921">
                  <div className="text-left min-w-0" data-id="element-922">
                    <div className="text-[9px] text-ink-light uppercase tracking-wider font-bold" data-id="element-923">
                      Los 2 productos
                    </div>
                    <div className="flex items-baseline gap-2 mt-0.5" data-id="element-924">
                      <motion.span animate={{
                    scale: [1, 1.04, 1]
                  }} transition={{
                    duration: 1.5,
                    repeat: Infinity
                  }} className="text-xl sm:text-2xl font-extrabold text-combo-green tabular-nums" data-id="element-925">
                        ${TWO_KITS_PRICE.toLocaleString('es-AR')}
                      </motion.span>
                      <span className="text-[11px] sm:text-xs text-ink-light line-through decoration-red-500 decoration-2 tabular-nums" data-id="element-926">
                        ${normalTwo.toLocaleString('es-AR')}
                      </span>
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0" data-id="element-927">
                    <div className="inline-flex items-center gap-1 bg-combo-green text-white text-[10px] font-bold px-2 py-0.5 rounded-full whitespace-nowrap" data-id="element-928">
                      Ahorrás ${savings.toLocaleString('es-AR')}
                    </div>
                    <div className="text-[9px] text-ink-light mt-1 flex items-center justify-end gap-1" data-id="element-929">
                      <Lock className="w-2.5 h-2.5 text-combo-green" data-id="element-930" />
                      Solo en esta pantalla
                    </div>
                  </div>
                </div>
              </div>
              <PaymentMethods className="mt-3" data-id="element-931" />
            </div>
            {/* CTAs */}
            <div className="px-4 sm:px-6 py-3 sm:py-3.5 bg-white border-t border-ink/5 space-y-1.5" data-id="element-932">
              <motion.button onClick={() => onAccept(chosenId, chosenOption?.type ?? 'kit')} whileHover={{
            scale: 1.02
          }} whileTap={{
            scale: 0.98
          }} animate={{
            boxShadow: ['0 0 0 0 rgba(27,67,50,0.55)', '0 0 0 14px rgba(27,67,50,0)', '0 0 0 0 rgba(27,67,50,0)']
          }} transition={{
            duration: 2,
            repeat: Infinity
          }} className="w-full bg-combo-green text-white py-2.5 sm:py-3 rounded-full font-extrabold text-[12px] sm:text-sm hover:bg-combo-green/90 transition-colors flex items-center justify-center gap-1.5 shadow-glow" data-id="element-933">
                <span className="truncate" data-id="element-934">
                  ¡SÍ! LLEVAR LOS 2 POR $
                  {TWO_KITS_PRICE.toLocaleString('es-AR')}
                </span>
                <ArrowRight className="w-3.5 h-3.5 sm:w-4 sm:h-4 flex-shrink-0" data-id="element-935" />
              </motion.button>
              <button onClick={onDecline} className="w-full text-ink-light hover:text-ink text-[11px] sm:text-xs font-medium underline underline-offset-4 transition-colors py-1" data-id="element-936">
                No gracias, continuar así →
              </button>
              <div className="flex items-center justify-center gap-3 text-[9px] sm:text-[10px] text-ink-light font-medium" data-id="element-937">
                <span className="flex items-center gap-1" data-id="element-938">
                  <Lock className="w-2.5 h-2.5 text-combo-green" data-id="element-939" /> Pago Seguro
                </span>
                <span className="flex items-center gap-1" data-id="element-940">
                  <Zap className="w-2.5 h-2.5 text-gold" data-id="element-941" /> Acceso Inmediato
                </span>
              </div>
            </div>
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}